/**
 * App-wide "you're viewing as another user" bar — the counterpart to
 * ClockedInBanner. Rendered once, in Layout, as a sibling of the header
 * (never inside it — the header is `h-14 overflow-hidden`), so it stays
 * visible on every page for the entire session, the same reason a running
 * punch does.
 *
 * `me.view_as` is set by the backend on every /me response while the
 * X-View-As-User-Id header is attached (see firebase_auth.py) — this banner
 * is purely a display of that field plus the Exit action; it enforces
 * nothing itself, since the actual read-only gate lives server-side.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { roleLabel } from "../constants/roles";

export default function ViewAsBanner() {
  const { me, stopViewAs } = useAuth();
  const navigate = useNavigate();
  const [exiting, setExiting] = useState(false);

  if (!me?.view_as) return null;

  async function exit() {
    if (exiting) return;
    setExiting(true);
    try {
      await stopViewAs();
      navigate("/dashboard");
    } finally {
      setExiting(false);
    }
  }

  return (
    <div
      className="shrink-0 bg-warning-50 border-b border-warning-200 px-4 py-2
                 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm"
    >
      <span className="flex items-center gap-2 font-medium text-warning-800">
        <span className="w-2 h-2 rounded-full bg-warning-600 shrink-0" aria-hidden="true" />
        Viewing as {me.name || me.email}
      </span>
      <span className="text-warning-700 text-xs">
        ({roleLabel(me.role)}) · Read-only — nothing you do here changes their account
      </span>
      <span className="text-warning-700 text-xs">
        You're actually {me.view_as.real_user_name || "a system admin"}
      </span>
      <button
        type="button"
        onClick={exit}
        disabled={exiting}
        className="ml-auto text-xs font-medium text-warning-800 underline
                   hover:text-warning-900 disabled:opacity-50"
      >
        {exiting ? "Exiting…" : "Exit View As"}
      </button>
    </div>
  );
}
